import React from 'react';

// Shimmer placeholder block. Use to approximate page/content shape while data
// loads instead of a bare centered spinner — gives a sense of layout and
// perceived speed. The shimmer animation is auto-disabled under
// prefers-reduced-motion (global rule in index.css), leaving a static block.
export function Skeleton({
    width = '100%',
    height = 16,
    radius,
    style,
}: {
    width?: number | string;
    height?: number | string;
    radius?: number | string;
    style?: React.CSSProperties;
}) {
    return (
        <div
            className="skeleton"
            aria-hidden="true"
            style={{ width, height, ...(radius !== undefined ? { borderRadius: radius } : {}), ...style }}
        />
    );
}

// A card-shaped skeleton (matches the .card surface).
export function SkeletonCard({ height = 120, style }: { height?: number | string; style?: React.CSSProperties }) {
    return <Skeleton height={height} radius="var(--radius-lg)" style={style} />;
}

// Generic full-page loading skeleton: a title bar + a few content cards.
// Drop-in replacement for a centered <div className="spinner spinner-lg" />.
export function PageSkeleton() {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <Skeleton width={220} height={28} />
            <Skeleton width={320} height={14} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
                <SkeletonCard height={110} />
                <SkeletonCard height={110} />
                <SkeletonCard height={110} />
            </div>
        </div>
    );
}

// N stacked text-line skeletons with a shorter last line.
export function SkeletonText({ lines = 3, gap = 8 }: { lines?: number; gap?: number }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
            {Array.from({ length: lines }).map((_, i) => (
                <Skeleton key={i} height={12} width={i === lines - 1 ? '60%' : '100%'} />
            ))}
        </div>
    );
}
